import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoCheckmarkDoneSharp, IoCheckmarkSharp } from "react-icons/io5";
import { formatRelativeTime, isCustomerMessage } from "./chatUtils.js";

const FALLBACK_LOGO = "https://cdn-icons-png.flaticon.com/128/8189/8189412.png";

/**
 * Get the assigned agent from a conversation.
 * Returns { name, avatar } or null.
 */
function getAssignedAgent(conversation) {
    const assigned = conversation.teamInfo?.assignedTo;
    if (assigned?._id) {
        return { name: assigned.name, avatar: assigned.avatar };
    }
    return null;
}

export function ConversationList({
    conversations,
    darkMode,
    isLoading,
    onSelect,
    typingUsers = {},
    currentShopId,
}) {
    // Separate current store vs other store conversations
    const currentStoreConvos = currentShopId
        ? conversations.filter(
              (c) =>
                  String(c.shop?._id || c.shop?.id) === String(currentShopId),
          )
        : conversations;
    const otherStoreConvos = currentShopId
        ? conversations.filter(
              (c) =>
                  String(c.shop?._id || c.shop?.id) !== String(currentShopId),
          )
        : [];

    const renderConversationItem = (conversation, index, isCurrentStore) => {
        const shopName = conversation.shop?.name || "Conversation";
        const unread = Number(conversation.unreadCount) || 0;
        const lastMsg = conversation.lastMessage;
        const lastMessageContent =
            lastMsg?.content || conversation.lastMessagePreview || "";
        const lastMessageTime = lastMsg?.createdAt || conversation.updatedAt;

        const isMe = lastMsg?.sender ? isCustomerMessage(lastMsg) : false;
        const isLastMsgRead =
            isMe &&
            (Boolean(lastMsg?.read) ||
                (Array.isArray(lastMsg?.readBy) && lastMsg.readBy.length > 0));
        const senderLabel = isMe ? "You" : lastMsg?.sender?.name;
        const isImageMsg = lastMsg?.type === "image" || lastMsg?.attachment?.url;
        const displayContent = isImageMsg && !lastMessageContent
            ? "Photo"
            : lastMessageContent;
        const lastMsgPreview = displayContent
            ? senderLabel
                ? `${senderLabel}: ${isImageMsg && !lastMessageContent ? "📷 " : ""}${displayContent}`
                : `${isImageMsg && !lastMessageContent ? "📷 " : ""}${displayContent}`
            : "No messages yet";

        const agent = getAssignedAgent(conversation);
        const isOnline = conversation.online === true;
        const typingUser = typingUsers[String(conversation.id)] || null;
        const avatarSrc =
            conversation.shop?.logo || agent?.avatar || FALLBACK_LOGO;
        const avatarAlt = shopName;

        return (
            <motion.button
                key={conversation.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                    duration: 0.2,
                    delay: index * 0.04,
                }}
                onClick={() => onSelect(conversation.id)}
                className="tw-w-full tw-text-left tw-px-3 tw-py-0 tw-border-0 tw-cursor-pointer tw-bg-transparent"
            >
                <div
                    className={`tw-flex tw-items-center tw-gap-3 tw-px-2 tw-py-3 tw-rounded-xl tw-transition-colors ${
                        isCurrentStore
                            ? darkMode
                                ? "hover:tw-bg-gray-800/60"
                                : "hover:tw-bg-gray-50"
                            : darkMode
                              ? "hover:tw-bg-gray-800/40"
                              : "hover:tw-bg-gray-50/80"
                    } ${!isCurrentStore ? "tw-opacity-75" : ""}`}
                >
                    {/* Assigned agent avatar */}
                    <div className="tw-relative tw-flex-shrink-0">
                        <div
                            className={`tw-w-11 tw-h-11 tw-rounded-full tw-overflow-hidden tw-ring-2 ${
                                unread > 0
                                    ? "tw-ring-primary/30"
                                    : darkMode
                                      ? "tw-ring-gray-700"
                                      : "tw-ring-gray-100"
                            }`}
                        >
                            <img
                                src={avatarSrc}
                                alt={avatarAlt}
                                className="tw-w-full tw-h-full tw-object-cover"
                                onError={(e) => {
                                    if (e.target.src !== FALLBACK_LOGO)
                                        e.target.src = FALLBACK_LOGO;
                                }}
                            />
                        </div>
                        <AnimatePresence>
                            {isOnline && (
                                <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 500,
                                        damping: 25,
                                    }}
                                    className={`tw-absolute tw-bottom-0 tw-right-0 tw-w-3 tw-h-3 tw-rounded-full tw-border-2 tw-bg-emerald-500 tw-z-[2] ${
                                        darkMode
                                            ? "tw-border-gray-900"
                                            : "tw-border-white"
                                    }`}
                                />
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Content */}
                    <div className="tw-flex-1 tw-min-w-0">
                        {/* Row 1: Agent/shop name + time */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
                                <span
                                    className={`tw-font-semibold tw-text-[13px] tw-truncate tw-leading-tight ${
                                        unread > 0
                                            ? darkMode
                                                ? "tw-text-white"
                                                : "tw-text-gray-900"
                                            : darkMode
                                              ? "tw-text-gray-200"
                                              : "tw-text-gray-800"
                                    }`}
                                >
                                    {shopName}
                                </span>
                                {agent?.name && (
                                    <span
                                        className="tw-text-[10px] tw-truncate tw-flex-shrink-0 tw-text-gray-400"
                                    >
                                        · {agent.name}
                                    </span>
                                )}
                            </div>
                            {lastMessageTime && (
                                <span
                                    className={`tw-text-[10px] tw-whitespace-nowrap tw-flex-shrink-0 tw-tabular-nums ${
                                        unread > 0
                                            ? "tw-text-primary tw-font-semibold"
                                            : darkMode
                                              ? "tw-text-gray-500"
                                              : "tw-text-gray-400"
                                    }`}
                                >
                                    {formatRelativeTime(lastMessageTime)}
                                </span>
                            )}
                        </div>

                        {/* Row 2: Last message / typing + unread badge */}
                        <div
                            className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mt-0.5"
                            style={{ minHeight: "18px" }}
                        >
                            {typingUser ? (
                                <div
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-flex-1 tw-min-w-0"
                                    style={{ height: "18px" }}
                                >
                                    <div className="tw-flex tw-items-center tw-space-x-0.5">
                                        <div
                                            className="tw-w-1 tw-h-1 tw-bg-primary tw-rounded-full tw-animate-bounce"
                                            style={{ animationDelay: "0ms" }}
                                        />
                                        <div
                                            className="tw-w-1 tw-h-1 tw-bg-primary tw-rounded-full tw-animate-bounce"
                                            style={{
                                                animationDelay: "150ms",
                                            }}
                                        />
                                        <div
                                            className="tw-w-1 tw-h-1 tw-bg-primary tw-rounded-full tw-animate-bounce"
                                            style={{
                                                animationDelay: "300ms",
                                            }}
                                        />
                                    </div>
                                    <span className="tw-text-[12px] tw-leading-[18px] tw-text-primary tw-font-medium tw-truncate">
                                        {typingUser.name || "Someone"} is
                                        typing...
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className="tw-flex tw-items-center tw-gap-1 tw-flex-1 tw-min-w-0"
                                    style={{ height: "18px" }}
                                >
                                    {isMe && lastMessageContent && (
                                        <span className="tw-flex-shrink-0 tw-flex tw-items-center">
                                            <AnimatePresence mode="wait">
                                                {isLastMsgRead ? (
                                                    <motion.span
                                                        key="read"
                                                        initial={{
                                                            scale: 0.3,
                                                            opacity: 0,
                                                        }}
                                                        animate={{
                                                            scale: 1,
                                                            opacity: 1,
                                                        }}
                                                        transition={{
                                                            type: "spring",
                                                            stiffness: 500,
                                                            damping: 20,
                                                        }}
                                                        className="tw-flex tw-items-center"
                                                    >
                                                        <IoCheckmarkDoneSharp
                                                            className="tw-text-emerald-500"
                                                            size={14}
                                                        />
                                                    </motion.span>
                                                ) : (
                                                    <motion.span
                                                        key="sent"
                                                        initial={{
                                                            scale: 0.3,
                                                            opacity: 0,
                                                        }}
                                                        animate={{
                                                            scale: 1,
                                                            opacity: 1,
                                                        }}
                                                        exit={{
                                                            scale: 0.3,
                                                            opacity: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.15,
                                                        }}
                                                        className="tw-flex tw-items-center"
                                                    >
                                                        <IoCheckmarkSharp
                                                            className={
                                                                darkMode
                                                                    ? "tw-text-gray-500"
                                                                    : "tw-text-gray-400"
                                                            }
                                                            size={14}
                                                        />
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </span>
                                    )}
                                    <p
                                        className={`tw-m-0 tw-text-[12px] tw-truncate tw-flex-1 tw-leading-[18px] ${
                                            unread > 0
                                                ? darkMode
                                                    ? "tw-text-gray-300 tw-font-medium"
                                                    : "tw-text-gray-700 tw-font-medium"
                                                : darkMode
                                                  ? "tw-text-gray-500"
                                                  : "tw-text-gray-500"
                                        }`}
                                    >
                                        {lastMsgPreview}
                                    </p>
                                </div>
                            )}
                            {unread > 0 && (
                                <span className="tw-bg-primary tw-text-white tw-text-[10px] tw-font-bold tw-min-w-[20px] tw-h-[20px] tw-px-1.5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                    {unread > 99 ? "99+" : unread}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </motion.button>
        );
    };

    return (
        <motion.div
            key="conversation-list"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={`tw-flex-1 tw-overflow-y-auto custom-scrollbar ${
                darkMode ? "tw-bg-dark" : "tw-bg-white"
            }`}
        >
            {isLoading && (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-gap-3">
                    <div className="tw-w-6 tw-h-6 tw-border-2 tw-border-primary/30 tw-border-t-primary tw-rounded-full tw-animate-spin" />
                    <span
                        className={`tw-text-xs tw-font-medium ${
                            darkMode ? "tw-text-gray-500" : "tw-text-gray-400"
                        }`}
                    >
                        Loading conversations
                    </span>
                </div>
            )}
            {!isLoading && conversations.length === 0 && (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-6 tw-gap-3">
                    <div
                        className={`tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center ${
                            darkMode ? "tw-bg-gray-800" : "tw-bg-gray-100"
                        }`}
                    >
                        <svg
                            className={`tw-w-6 tw-h-6 ${
                                darkMode
                                    ? "tw-text-gray-600"
                                    : "tw-text-gray-400"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                        </svg>
                    </div>
                    <p
                        className={`tw-text-sm tw-font-medium tw-m-0 ${
                            darkMode ? "tw-text-gray-400" : "tw-text-gray-500"
                        }`}
                    >
                        No conversations yet
                    </p>
                    <p
                        className={`tw-text-xs tw-m-0 tw-text-center ${
                            darkMode ? "tw-text-gray-600" : "tw-text-gray-400"
                        }`}
                    >
                        Start a new conversation and we'll reply as soon as we
                        can.
                    </p>
                </div>
            )}
            {!isLoading && conversations.length > 0 && (
                <div className="tw-py-1">
                    {/* Current store conversations */}
                    <AnimatePresence initial={false}>
                        {currentStoreConvos.map((conversation, index) =>
                            renderConversationItem(conversation, index, true),
                        )}
                    </AnimatePresence>

                    {/* Other store conversations */}
                    {otherStoreConvos.length > 0 && (
                        <>
                            <div
                                className={`tw-flex tw-items-center tw-gap-2 tw-px-5 tw-pt-3 tw-pb-1 ${
                                    currentStoreConvos.length > 0
                                        ? "tw-mt-1"
                                        : ""
                                }`}
                            >
                                <div
                                    className={`tw-flex-1 tw-h-px ${
                                        darkMode
                                            ? "tw-bg-gray-800"
                                            : "tw-bg-gray-100"
                                    }`}
                                />
                                <span
                                    className={`tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-whitespace-nowrap tw-flex-shrink-0 ${
                                        darkMode
                                            ? "tw-text-gray-600"
                                            : "tw-text-gray-400"
                                    }`}
                                >
                                    Other stores
                                </span>
                                <div
                                    className={`tw-flex-1 tw-h-px ${
                                        darkMode
                                            ? "tw-bg-gray-800"
                                            : "tw-bg-gray-100"
                                    }`}
                                />
                            </div>
                            <AnimatePresence initial={false}>
                                {otherStoreConvos.map((conversation, index) =>
                                    renderConversationItem(
                                        conversation,
                                        index + currentStoreConvos.length,
                                        false,
                                    ),
                                )}
                            </AnimatePresence>
                        </>
                    )}
                </div>
            )}
        </motion.div>
    );
}
